/**
 * Role Utilities - Centralized role checking functions
 * 
 * Provides consistent role checking across the application.
 * All admin role checks should use these utilities to ensure
 * super_admin is always included.
 */

/**
 * Check if user has any admin role
 * @param {Array<string>} userRoles - Array of user roles
 * @returns {boolean} True if user has any admin role
 */
export function isAdmin(userRoles = []) {
  if (!Array.isArray(userRoles)) return false;
  
  const adminRoles = [
    'super_admin',
    'hr_admin',
    'school_admin',
    'Admin',
    'HR_Staff',
    'HR',
    'Administrator'
  ];
  
  return userRoles.some(role => adminRoles.includes(role));
}

/**
 * Check if user can manage absences (view, create, approve)
 * @param {Array<string>} userRoles - Array of user roles
 * @returns {boolean} True if user can manage absences
 */
export function canManageAbsences(userRoles = []) {
  return isAdmin(userRoles);
}

/**
 * Check if user can approve absences
 * @param {Array<string>} userRoles - Array of user roles
 * @returns {boolean} True if user can approve absences
 */
export function canApproveAbsences(userRoles = []) {
  return isAdmin(userRoles);
}

/**
 * Check if user can edit employees
 * @param {Array<string>} userRoles - Array of user roles
 * @returns {boolean} True if user can edit employees
 */
export function canEditEmployees(userRoles = []) {
  return isAdmin(userRoles);
}

/**
 * Check if user is Super Admin specifically
 * @param {Array<string>} userRoles - Array of user roles
 * @returns {boolean} True if user is Super Admin
 */
export function isSuperAdmin(userRoles = []) {
  if (!Array.isArray(userRoles)) return false;
  return userRoles.includes('super_admin');
}

/**
 * Check if user is HR Admin or Super Admin
 * @param {Array<string>} userRoles - Array of user roles
 * @returns {boolean} True if user is HR Admin or Super Admin
 */
export function isHROrSuperAdmin(userRoles = []) {
  if (!Array.isArray(userRoles)) return false;
  return userRoles.includes('super_admin') || 
         userRoles.includes('hr_admin') || 
         userRoles.includes('Admin') || 
         userRoles.includes('HR_Staff');
}

